import dayjs from 'dayjs';
import { useEffect, useRef, useState } from 'react';
import AxiosUtils from 'utils/axios-utils';
import { API } from 'service/api-route';
import { colors } from 'theme/tokens';
import { SupportMeta } from './types';

const GENERIC = /^(Bad Request|Not Found|Conflict|Unprocessable Entity|Forbidden|Unauthorized|Internal Server Error|Service Unavailable)$/i;

/**
 * Câu lỗi tiếng Việt từ backend. SupportError gửi câu ở `error`, mã ở `message`;
 * lỗi class-validator gửi mảng ở `message`. Interceptor axios ném `response.data`.
 */
export function supportError(err: any, fallback = 'Có lỗi xảy ra, vui lòng thử lại.'): string {
  if (err?.networkError) return 'Không kết nối được máy chủ.';
  if (typeof err?.error === 'string' && err.error && !GENERIC.test(err.error)) return err.error;
  if (Array.isArray(err?.message)) return err.message.join('; ');
  const status = err?.statusCode || err?.status;
  if (status === 503) return 'Máy chủ chưa có bảng dữ liệu hỗ trợ — cần chạy migration 20260928000700_support_inbox.sql.';
  if (status >= 500) return 'Lỗi máy chủ, vui lòng thử lại sau.';
  if (typeof err?.message === 'string' && err.message && !/^[A-Z_]+$/.test(err.message)) return err.message;
  return fallback;
}

/** "09:12" hôm nay, "Hôm qua 21:03", "28/09 08:15", khác năm "28/09/25". */
export function chatTime(value?: string | null): string {
  if (!value) return '';
  const t = dayjs(value);
  const now = dayjs();
  if (t.isSame(now, 'day')) return t.format('HH:mm');
  if (t.isSame(now.subtract(1, 'day'), 'day')) return `Hôm qua ${t.format('HH:mm')}`;
  if (t.isSame(now, 'year')) return t.format('DD/MM HH:mm');
  return t.format('DD/MM/YY HH:mm');
}

/** "5 phút", "2 giờ 10 phút", "3 ngày". */
export function durationText(fromIso?: string | null, now = Date.now()): string {
  if (!fromIso) return '';
  const minutes = Math.max(0, Math.floor((now - new Date(fromIso).getTime()) / 60000));
  if (minutes < 1) return 'vừa xong';
  if (minutes < 60) return `${minutes} phút`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return minutes % 60 ? `${hours} giờ ${minutes % 60} phút` : `${hours} giờ`;
  return `${Math.floor(hours / 24)} ngày`;
}

/** Màu cảnh báo theo thời gian chờ: < 15 phút bình thường, < 60 phút cam, còn lại đỏ. */
export function waitingTone(fromIso?: string | null, now = Date.now()): { color: string; bg: string } {
  if (!fromIso) return { color: colors.textMuted, bg: 'transparent' };
  const minutes = (now - new Date(fromIso).getTime()) / 60000;
  if (minutes < 15) return { color: colors.infoInk, bg: colors.infoSoft };
  if (minutes < 60) return { color: colors.warningInk, bg: colors.warningSoft };
  return { color: colors.dangerInk, bg: colors.dangerSoft };
}

export const STATUS_STYLE: Record<string, { label: string; color: string; bg: string }> = {
  OPEN: { label: 'Chờ trả lời', color: colors.dangerInk, bg: colors.dangerSoft },
  PENDING: { label: 'Chờ người dùng', color: colors.infoInk, bg: colors.infoSoft },
  RESOLVED: { label: 'Đã xong', color: colors.successInk, bg: colors.successSoft },
};

export const PRIORITY_STYLE: Record<string, { label: string; color: string; bg: string }> = {
  NORMAL: { label: 'Bình thường', color: colors.textSecondary, bg: colors.surfaceMuted },
  HIGH: { label: 'Cao', color: colors.warningInk, bg: colors.warningSoft },
  URGENT: { label: 'Khẩn', color: colors.dangerInk, bg: colors.dangerSoft },
};

export const CLASS_STYLE: Record<string, { color: string; bg: string }> = {
  MANAGEMENT: { color: colors.accentDeep, bg: colors.accentSoft },
  AGENCY: { color: colors.starInk, bg: colors.starSoft },
  CREATOR: { color: colors.purpleInk, bg: colors.purpleSoft },
  CREATOR_NPC: { color: colors.purpleInk, bg: colors.purpleSoft },
  STAFF: { color: colors.infoInk, bg: colors.infoSoft },
  PUBLIC: { color: colors.textSecondary, bg: colors.surfaceMuted },
};

export const SOURCE_LABEL: Record<string, string> = {
  AGENT: '',
  TEMPLATE: 'Văn mẫu',
  AUTO: 'Tự động ngoài giờ',
  WELCOME: 'Tin chào',
  SYSTEM: 'Không qua hộp thư',
};

/** Thay {bien} bằng giá trị (giống renderTemplate phía backend). */
export function renderTemplate(content: string, variables: Record<string, string>): string {
  return (content || '').replace(/\{([a-z0-9_]+)\}/g, (match, key: string) =>
    key in variables ? variables[key] ?? '' : match
  );
}

/** Chữ cái đầu cho ảnh đại diện trống. */
export function initials(name?: string | null): string {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  const last = parts[parts.length - 1];
  return (parts.length > 1 ? parts[0][0] + last[0] : last.slice(0, 2)).toUpperCase();
}

// ------------------------------------------------------------- hooks

let metaCache: Promise<SupportMeta | null> | null = null;

/** Danh mục trạng thái/loại vấn đề/biến văn mẫu — tải một lần mỗi phiên. */
export function useSupportMeta(): SupportMeta | null {
  const [meta, setMeta] = useState<SupportMeta | null>(null);
  useEffect(() => {
    if (!metaCache) {
      metaCache = AxiosUtils.get(API.supportMeta)
        .then((res: any) => res?.data as SupportMeta)
        .catch(() => {
          metaCache = null;
          return null;
        });
    }
    let alive = true;
    metaCache.then((value) => alive && setMeta(value));
    return () => {
      alive = false;
    };
  }, []);
  return meta;
}

/**
 * Gọi `fn` mỗi `ms` mili giây khi tab đang hiện, và gọi ngay khi quay lại tab.
 * `fn` mới nhất luôn được dùng (không cần đưa vào deps).
 */
export function usePolling(fn: () => void, ms: number, enabled = true) {
  const ref = useRef(fn);
  ref.current = fn;
  useEffect(() => {
    if (!enabled) return undefined;
    const tick = () => {
      if (document.visibilityState === 'visible') ref.current();
    };
    const timer = window.setInterval(tick, ms);
    document.addEventListener('visibilitychange', tick);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', tick);
    };
  }, [ms, enabled]);
}

/** Tiếng "ting" ngắn bằng WebAudio (không cần tệp âm thanh). Trình duyệt chặn thì im lặng. */
export function playChime() {
  try {
    const Ctx = (window as any).AudioContext || (window as any).webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const gain = ctx.createGain();
    gain.connect(ctx.destination);
    gain.gain.setValueAtTime(0.0001, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.18, ctx.currentTime + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.5);
    [880, 1318.5].forEach((freq, i) => {
      const osc = ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.value = freq;
      osc.connect(gain);
      osc.start(ctx.currentTime + i * 0.12);
      osc.stop(ctx.currentTime + 0.5 + i * 0.12);
    });
    window.setTimeout(() => ctx.close?.(), 1200);
  } catch {
    // Không phát được âm thanh thì thôi.
  }
}

const SOUND_KEY = 'support.sound';
export function readSoundPref(): boolean {
  try {
    return localStorage.getItem(SOUND_KEY) !== '0';
  } catch {
    return true;
  }
}
export function writeSoundPref(on: boolean) {
  try {
    localStorage.setItem(SOUND_KEY, on ? '1' : '0');
  } catch {
    // bỏ qua
  }
}
